/**
 * ShotHeatMap — SVG court overlay showing per-zone FG% + attempts.
 *
 * Designed as a lightweight insertable component (no Layout wrapper) so it
 * can drop into the existing Shot Analyzer page or a player profile.
 * The court is a top-down right-half (free-throw line at the top, baseline
 * at the bottom — rotated for a more familiar shooting view).
 *
 * Zones (matching backend `routes_shot_analyzer.heatmap`):
 *   left_corner_3, left_wing_3, top_3, right_wing_3, right_corner_3,
 *   mid_range, paint_ft.
 *
 * Color scale: cold blue (≤25%) → neutral (≈40%) → hot red (≥70%).
 */
import React from 'react';
import { api } from '@/lib/api';
import { Flame, Loader2 } from 'lucide-react';

const ZONE_LABELS = {
  left_corner_3:  'Esquina izq.',
  left_wing_3:    'Wing izq.',
  top_3:          'Tope',
  right_wing_3:   'Wing der.',
  right_corner_3: 'Esquina der.',
  mid_range:      'Media distancia',
  paint_ft:       'Tiro libre / pintura',
};

// Polygon coords for each zone on a 100x100 court SVG (half-court, basket
// at bottom). Coords picked to roughly match real FIBA spacing.
const ZONE_PATHS = {
  // Below the 3-pt arc (paint + FT line area)
  paint_ft:       'M 35,75 L 65,75 L 65,100 L 35,100 Z',
  // Mid-range donut
  mid_range:      'M 22,55 Q 22,30 50,30 Q 78,30 78,55 L 78,75 L 65,75 L 65,100 L 35,100 L 35,75 L 22,75 Z',
  // Three-pointer slices
  left_corner_3:  'M 0,55 L 22,55 L 22,100 L 0,100 Z',
  right_corner_3: 'M 78,55 L 100,55 L 100,100 L 78,100 Z',
  left_wing_3:    'M 0,55 Q 0,30 22,18 L 35,28 Q 22,32 22,55 Z',
  right_wing_3:   'M 100,55 Q 100,30 78,18 L 65,28 Q 78,32 78,55 Z',
  top_3:          'M 22,18 Q 30,5 50,5 Q 70,5 78,18 L 65,28 Q 55,18 50,18 Q 45,18 35,28 Z',
};

// Centroids for label placement.
const ZONE_CENTROIDS = {
  paint_ft:       [50, 88],
  mid_range:      [50, 55],
  left_corner_3:  [11, 80],
  right_corner_3: [89, 80],
  left_wing_3:    [12, 38],
  right_wing_3:   [88, 38],
  top_3:          [50, 18],
};

function heatColor(pct, attempts) {
  if (!attempts) return '#1F2937';  // neutral gray — no data
  // Blend cold → hot. 0% = blue, 40% = neutral gold, 70%+ = red.
  if (pct <= 25) return '#2563EB';
  if (pct <= 40) return '#0EA5E9';
  if (pct <= 55) return '#D4AF37';
  if (pct <= 70) return '#F97316';
  return '#DC2626';
}

export default function ShotHeatMap({ playerId, days = 90 }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = { days };
    if (playerId) params.player_id = playerId;
    api.get('/shot-analyzer/heatmap', { params })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Sin datos'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [playerId, days]);

  if (loading) {
    return (
      <div className="rounded-xl border border-gold/20 bg-navy-deep p-6 grid place-items-center min-h-[260px]">
        <Loader2 className="w-5 h-5 animate-spin text-gold" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-xl border border-gold/20 bg-navy-deep p-6 text-center text-xs font-mono uppercase tracking-widest text-slate-400">
        {error || 'Sin datos'}
      </div>
    );
  }

  const byZone = Object.fromEntries((data.zones || []).map((z) => [z.zone, z]));

  return (
    <div className="rounded-xl border border-gold/20 bg-navy-deep p-5" data-testid="shot-heatmap">
      <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold">Shot Heat Map</div>
          <h3 className="font-h text-xl uppercase">Dónde anota mejor</h3>
        </div>
        <div className="text-right text-[11px] font-mono uppercase tracking-widest text-slate-400">
          {data.total_attempts} tiros · últimos {data.days} días
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
        {/* Court SVG */}
        <div className="md:col-span-3">
          <svg viewBox="0 0 100 100" className="w-full max-w-md mx-auto block">
            {/* Court bg */}
            <rect x="0" y="0" width="100" height="100" fill="#0A192F" />
            {/* Zones */}
            {Object.entries(ZONE_PATHS).map(([key, d]) => {
              const z = byZone[key] || { attempts: 0, fg_pct: 0 };
              const fill = heatColor(z.fg_pct, z.attempts);
              return (
                <path
                  key={key}
                  d={d}
                  fill={fill}
                  fillOpacity={z.attempts ? 0.65 : 0.2}
                  stroke="#D4AF37"
                  strokeWidth="0.4"
                  data-testid={`heat-zone-${key}`}
                >
                  <title>{ZONE_LABELS[key]}: {z.fg_pct}% sobre {z.attempts}</title>
                </path>
              );
            })}
            {/* Zone labels — FG% if has data, else dash */}
            {Object.entries(ZONE_CENTROIDS).map(([key, [cx, cy]]) => {
              const z = byZone[key] || { attempts: 0, fg_pct: 0 };
              return (
                <g key={key} pointerEvents="none">
                  <text x={cx} y={cy - 1.5} textAnchor="middle" fontSize="3.5" fill="#FFFFFF" fontWeight="700" fontFamily="Arial">
                    {z.attempts ? `${z.fg_pct}%` : '—'}
                  </text>
                  <text x={cx} y={cy + 2.5} textAnchor="middle" fontSize="2.2" fill="#FFFFFF" opacity="0.85" fontFamily="Arial">
                    {z.attempts ? `${z.made}/${z.attempts}` : ''}
                  </text>
                </g>
              );
            })}
            {/* Rim */}
            <circle cx="50" cy="93" r="2.4" fill="none" stroke="#FB923C" strokeWidth="0.8" />
            <line x1="44" y1="91" x2="56" y2="91" stroke="#FFFFFF" strokeWidth="0.6" />
          </svg>
          {/* Color legend */}
          <div className="mt-3 flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400">
            <span>Frío</span>
            <div className="flex h-2 rounded overflow-hidden">
              {['#2563EB','#0EA5E9','#D4AF37','#F97316','#DC2626'].map((c) => (
                <div key={c} className="w-6 h-2" style={{ backgroundColor: c }} />
              ))}
            </div>
            <span>Caliente</span>
          </div>
        </div>

        {/* Numeric leaderboard */}
        <div className="md:col-span-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Top zonas</div>
          <ul className="space-y-1.5 text-sm" data-testid="heat-leaderboard">
            {(data.zones || []).filter((z) => z.attempts > 0).slice(0, 7).map((z) => (
              <li key={z.zone} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md bg-navy border border-white/10">
                <div className="flex items-center gap-2 min-w-0">
                  <Flame
                    className="w-3.5 h-3.5 flex-shrink-0"
                    style={{ color: heatColor(z.fg_pct, z.attempts) }}
                  />
                  <span className="truncate text-slate-100">{ZONE_LABELS[z.zone]}</span>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="text-gold font-mono">{z.fg_pct}%</div>
                  <div className="text-[10px] text-slate-500 font-mono">{z.made}/{z.attempts}</div>
                </div>
              </li>
            ))}
            {(data.zones || []).every((z) => !z.attempts) && (
              <li className="text-xs italic text-slate-500 px-2 py-3 text-center">
                Sin tiros registrados todavía. Cuando hagas el primer Shot Analyzer aparece acá.
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
